import { useState, useEffect, useCallback } from 'react';
import { flows } from '../services/api';
import { FlowCompletionStatus } from '../types';

interface UseFlowCompletionStatusResult {
  completionStatus: FlowCompletionStatus | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to check if a flow has been completed and when it was last completed
 * @param flowName The name of the flow to check
 * @param fallbackToUserData If true, will use user.has_completed_intake as fallback when API fails
 * @returns Object containing completionStatus, loading state, error state, and refetch function
 */
export const useFlowCompletionStatus = (
  flowName: string,
  fallbackToUserData: boolean = false
): UseFlowCompletionStatusResult => {
  const [completionStatus, setCompletionStatus] = useState<FlowCompletionStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState<boolean>(false);

  const fetchCompletionStatus = useCallback(async () => {
    if (hasAttemptedFetch) {
      // Prevent multiple fetch attempts if we've already tried and failed
      return;
    }
    
    try {
      setLoading(true);
      const status = await flows.getCompletionStatus(flowName);
      setCompletionStatus(status);
      setError(null);
    } catch (err) {
      console.error('Error fetching flow completion status:', err);
      setError('Failed to fetch flow completion status');
      
      // If API call fails and we're using fallback, create a fallback status
      if (fallbackToUserData) {
        const userData = localStorage.getItem('user');
        if (userData) {
          try {
            const user = JSON.parse(userData);
            if (user && typeof user.has_completed_intake === 'boolean') {
              // Use user data as fallback
              setCompletionStatus({
                completed_at: user.has_completed_intake ? new Date().toISOString() : null,
                flow_name: flowName
              });
            }
          } catch (parseErr) {
            console.error('Error parsing user data for fallback:', parseErr);
          }
        }
      }
    } finally {
      setLoading(false);
      setHasAttemptedFetch(true);
    }
  }, [fallbackToUserData, flowName, hasAttemptedFetch]);

  useEffect(() => {
    fetchCompletionStatus();
  }, [fetchCompletionStatus]);

  return {
    completionStatus,
    loading,
    error,
    refetch: fetchCompletionStatus
  };
};

export default useFlowCompletionStatus;
